import React, { useState } from "react";
import { TotpMultiFactorGenerator } from "firebase/auth";

import PathwayTextInputGroup from "../../../_UI/Components/Inputs/PathwayTextInputGroup";
import Spinner from "./Spinner";

/**
 * 2-factor code entry for the SSO registration flow. Resolves the TOTP challenge against the
 * resolver stashed on window.__mfaResolver (by registrationSso), then hands the resulting Firebase
 * token back to the normal SSO registration path via onResolved — so existing-account handling and
 * navigation behave exactly like a non-2FA SSO signup. No redirect to /login.
 */
const MfaPrompt = ({ provider, onResolved, onCancel }) => {
    const [code, setCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const valid = /^\d{6}$/.test(code);

    const submit = async (e) => {
        e.preventDefault();
        if (!valid || submitting) return;
        setError(null);
        setSubmitting(true);
        try {
            const resolver = window.__mfaResolver;
            if (!resolver || !resolver.hints || resolver.hints.length === 0) {
                throw new Error("missing-resolver");
            }
            const assertion = TotpMultiFactorGenerator.assertionForSignIn(resolver.hints[0].uid, code);
            const result = await resolver.resolveSignIn(assertion);
            const idToken = await result.user.getIdToken();
            const tokenResult = await result.user.getIdTokenResult();
            // Feed the verified session into the normal SSO flow (verify → existing-account handling
            // / create / login). On success it advances the flow or navigates away.
            await onResolved({
                provider,
                idToken,
                expirationTime: tokenResult.expirationTime,
                refreshToken: result.user.refreshToken,
                email: result.user.email,
                displayName: result.user.displayName,
            });
        } catch (err) {
            if (err?.code === "auth/invalid-verification-code") {
                setError("Der eingegebene Bestätigungscode ist ungültig. Bitte versuche es erneut.");
            } else if (err?.code === "auth/code-expired") {
                setError("Der Bestätigungscode ist abgelaufen. Bitte starte die Anmeldung erneut.");
            } else {
                setError("Fehler bei der 2-Faktor-Authentifizierung. Bitte versuche es erneut.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="registration-prompt-overlay" data-cy="registration-mfa-prompt">
            <form className="registration-prompt" role="dialog" aria-modal="true" aria-labelledby="registration-mfa-title" onSubmit={submit}>
                <h2 className="registration-prompt__title" id="registration-mfa-title">
                    2-Faktor-Authentifizierung
                </h2>
                <p className="registration-prompt__text">
                    Dein Konto ist mit 2-Faktor-Authentifizierung geschützt. Gib den 6-stelligen Code aus deiner
                    Authenticator-App ein.
                </p>

                <PathwayTextInputGroup
                    type="text"
                    name="mfaCode"
                    label="2-Faktor-Code"
                    placeHolder="6-stelliger Code aus der Authenticator-App"
                    dataCy="registration-mfa-code"
                    value={code}
                    handleChange={(e) => setCode(e.target.value)}
                    maxLength="6"
                    disabled={submitting}
                    autoFocus
                />

                {error && (
                    <p className="registration-input__error" role="alert">
                        {error}
                    </p>
                )}

                <button type="submit" className="registration-cta" disabled={!valid || submitting} data-cy="registration-mfa-submit">
                    {submitting ? (
                        <>
                            <Spinner /> Wird geprüft …
                        </>
                    ) : (
                        "Code bestätigen"
                    )}
                </button>
                <button type="button" className="registration-sso" onClick={onCancel} disabled={submitting} data-cy="registration-mfa-cancel">
                    Abbrechen
                </button>
            </form>
        </div>
    );
};

export default MfaPrompt;
